import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { API } from "@/lib/api";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById("root");
const app = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

const seed = (key, path) =>
  API.get(path)
    .then(({ data }) => queryClient.setQueryData(key, data))
    .catch(() => null);

async function seedQueryCache(pathname) {
  const jobs = [
    seed(["settings"], "/settings"),
    seed(["services"], "/services"),
    seed(["areas"], "/areas"),
  ];
  if (pathname === "/") {
    jobs.push(
      seed(["projects", { featured: true }], "/projects?featured=true"),
      seed(["testimonials"], "/testimonials")
    );
  } else if (pathname.startsWith("/areas-we-cover/")) {
    const slug = pathname.split("/").filter(Boolean).pop();
    jobs.push(
      seed(["area", slug], `/areas/${slug}`),
      seed(["projects", {}], "/projects"),
      seed(["testimonials"], "/testimonials"),
      seed(["articles"], "/articles")
    );
  } else if (pathname === "/areas-we-cover") {
    // covered by common seeds
  } else if (pathname.startsWith("/services/")) {
    const slug = pathname.split("/").filter(Boolean).pop();
    jobs.push(
      seed(["service", slug], `/services/${slug}`),
      seed(["projects", {}], "/projects"),
      seed(["articles"], "/articles")
    );
  } else if (pathname === "/projects") {
    jobs.push(seed(["projects", {}], "/projects"));
  } else if (pathname.startsWith("/projects/")) {
    const slug = pathname.split("/").filter(Boolean).pop();
    jobs.push(seed(["project", slug], `/projects/${slug}`), seed(["services"], "/services"));
  } else if (pathname === "/insights") {
    jobs.push(seed(["articles"], "/articles"));
  } else if (pathname.startsWith("/insights/")) {
    const slug = pathname.split("/").filter(Boolean).pop();
    jobs.push(seed(["article", slug], `/articles/${slug}`));
  } else if (pathname === "/" || pathname === "/request-a-consultation") {
    jobs.push(seed(["testimonials"], "/testimonials"));
  }
  await Promise.all(jobs);
}

// Hydrate prerendered HTML instead of clearing it. Seed React Query first so
// the first client render matches the snapshot and the visible LCP is preserved.
const HYDRATION_MISMATCH = /#41[08]|#42[0-9]|does not match|Hydration/i;

async function boot() {
  if (rootElement.hasChildNodes()) {
    window.__NOOR_PRERENDERED__ = true;
    await seedQueryCache(window.location.pathname);
    ReactDOM.hydrateRoot(rootElement, app, {
      // Snapshots are captured at build time; live data (daily-published
      // articles, updated timestamps) and runtime animation state can
      // legitimately differ at hydration. React recovers these mismatches
      // surgically per node — filter the benign #418/#42x noise out of the
      // console while surfacing genuinely unexpected errors.
      onRecoverableError: (error) => {
        const msg = String((error && error.message) || error);
        if (HYDRATION_MISMATCH.test(msg)) return;
        console.error(error);
      },
    });
    // React 18 hydrates ASYNCHRONOUSLY — the first hydration render happens
    // after hydrateRoot() returns. Keep the flag up through that render so
    // motion components mount in their final state (matching the snapshot —
    // otherwise the page-transition wrapper starts at opacity:0, React throws
    // hydration error #418 and re-renders with a visible content flash).
    // 2s later the flag drops so client-side navigations animate normally.
    setTimeout(() => {
      window.__NOOR_PRERENDERED__ = false;
    }, 2000);
  } else {
    ReactDOM.createRoot(rootElement).render(app);
  }
}

boot();
